class CartDrawerTools extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onCartUpdated = this.onCartUpdated.bind(this);

    this.classListMap = {
      "btn-add-note": ".add-note",
      "btn-add-coupon": ".add-coupon",
      "btn-estimate-shipping": ".estimate-shipping",
      "btn-add-gift": ".add-gift",
    };

    this._noteCache = null;
    this._bound = false;
    this._syncTimer = null;
  }

  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    document.addEventListener("click", this.onClick);
    document.addEventListener("submit", this.onSubmit, true);
    document.addEventListener("change", this.onChange, true);

    // ✅ sync from cart page updates
    window.addEventListener("tf:cart:updated", this.onCartUpdated);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onClick);
    document.removeEventListener("submit", this.onSubmit, true);
    document.removeEventListener("change", this.onChange, true);

    window.removeEventListener("tf:cart:updated", this.onCartUpdated);

    this._bound = false;
    clearTimeout(this._syncTimer);
  }

  async onCartUpdated() {
    // chỉ refresh khi drawer đang mở
    const drawer = document.querySelector("cart-drawer");
    const isOpen =
      drawer &&
      (drawer.classList.contains("active") ||
        drawer.hasAttribute("open") ||
        drawer.getAttribute("aria-hidden") === "false");

    if (!isOpen) return;

    clearTimeout(this._syncTimer);
    this._syncTimer = setTimeout(async () => {
      try {
        await this.refreshDrawer();
      } catch (err) {
        console.error("[CartDrawerTools] sync refresh failed", err);
      }
    }, 120);
  }

  // ===== settings (fallback) =====
  get giftVariantId() {
    return Number(this.dataset.giftVariantId || 0);
  }

  // ===== ui helpers =====
  get allPopups() {
    return Array.from(document.querySelectorAll(".tf-mini-cart-tool-openable"));
  }

  closeAllPopups() {
    this.allPopups.forEach((el) => el.classList.remove("open"));
  }

  openPopup(selector) {
    this.closeAllPopups();
    const el = document.querySelector(selector);
    if (el) el.classList.add("open");
  }

  setBusy(el, busy) {
    if (!el) return;
    el.style.pointerEvents = busy ? "none" : "";
    el.style.opacity = busy ? "0.65" : "";
    el.setAttribute("aria-busy", busy ? "true" : "false");
  }

  // ===== re-render drawer =====
  async refreshDrawer() {
    const res = await fetch("/?sections=cart-drawer", { credentials: "same-origin" });
    const sections = await res.json();

    if (sections["cart-drawer"]) {
      const doc = new DOMParser().parseFromString(sections["cart-drawer"], "text/html");
      const newDrawer = doc.querySelector("#CartDrawer");
      const curDrawer = document.querySelector("#CartDrawer");
      if (newDrawer && curDrawer) curDrawer.innerHTML = newDrawer.innerHTML;
    }

    window.updateCartCount?.();
    document.querySelector("cart-drawer")?.open?.();
  }

  // =========================
  // GIFT helpers
  // =========================
  getGiftVariantIdFromPopup(popup) {
    const fromPopup = Number(popup?.dataset?.giftVariantId || 0);
    if (fromPopup) return fromPopup;

    const btn = popup?.querySelector("[data-gift-submit]");
    const fromBtn = Number(btn?.dataset?.giftVariantId || 0);
    if (fromBtn) return fromBtn;

    return Number(this.giftVariantId || 0);
  }

  async cartJSON() {
    return fetch("/cart.js", { credentials: "same-origin" }).then((r) => r.json());
  }

  async getGiftItem(giftVariantId) {
    const cart = await this.cartJSON();
    return (cart.items || []).find((it) => Number(it.variant_id) === Number(giftVariantId)) || null;
  }

  async updateGiftButton(popup) {
    const giftVariantId = this.getGiftVariantIdFromPopup(popup);
    if (!giftVariantId) return;

    const btn = popup.querySelector("[data-gift-submit]") || popup.querySelector("button[type='submit']");
    if (!btn) return;

    const giftItem = await this.getGiftItem(giftVariantId);
    if (giftItem) {
      btn.textContent = "Remove Gift Wrap";
      btn.dataset.giftMode = "remove";
    } else {
      btn.textContent = "Add a Gift Wrap";
      btn.dataset.giftMode = "add";
    }
  }

  // ===== SHIPPING: update province when country changes =====
  onChange(e) {
    if (!e.target.closest("cart-drawer")) return;
    if (e.target.id !== "shipping-country-form") return;

    const countrySelect = e.target;
    const popup = countrySelect.closest(".tf-mini-cart-tool-openable.estimate-shipping");
    if (!popup) return;

    const provinceSelect = popup.querySelector("#shipping-province-form");
    if (!provinceSelect) return;

    const provinceField = provinceSelect.closest(".field");
    const selectedOption = countrySelect.options[countrySelect.selectedIndex];
    const provincesJson = selectedOption?.getAttribute("data-provinces") || "[]";

    let provinces = [];
    try {
      provinces = JSON.parse(provincesJson) || [];
    } catch (_) {
      provinces = [];
    }

    provinceSelect.innerHTML = "";

    if (provinces.length) {
      const def = document.createElement("option");
      def.value = "";
      def.textContent = "---";
      provinceSelect.appendChild(def);

      provinces.forEach((pair) => {
        const name = pair?.[0];
        if (!name) return;
        const opt = document.createElement("option");
        opt.value = name;
        opt.textContent = name;
        provinceSelect.appendChild(opt);
      });

      if (provinceField) provinceField.style.display = "";
    } else {
      if (provinceField) provinceField.style.display = "none";
    }
  }

  // ===== click open/close =====
  onClick(e) {
    if (!e.target.closest("cart-drawer")) return;

    if (e.target.closest(".tf-mini-cart-tool-close")) {
      e.preventDefault?.();
      this.closeAllPopups();
      return;
    }

    const btn = e.target.closest(".btn-add-note, .btn-add-coupon, .btn-estimate-shipping, .btn-add-gift");
    if (!btn) return;

    e.preventDefault();

    if (btn.classList.contains("btn-add-gift")) {
      this.openPopup(this.classListMap["btn-add-gift"]);
      const popup = document.querySelector(".tf-mini-cart-tool-openable.add-gift");
      if (popup) this.updateGiftButton(popup);
      return;
    }

    if (btn.classList.contains("btn-add-note")) {
      this.openPopup(this.classListMap["btn-add-note"]);

      const noteTextarea =
        document.querySelector(".tf-mini-cart-tool-openable.add-note #Cart-note") ||
        document.querySelector(".tf-mini-cart-tool-openable.add-note textarea[name='note']");

      if (noteTextarea) {
        fetch("/cart.js", { credentials: "same-origin" })
          .then((r) => r.json())
          .then((cart) => {
            noteTextarea.value = cart.note || "";
            this._noteCache = cart.note || "";
          })
          .catch(console.error);
      }
      return;
    }

    for (const btnClass in this.classListMap) {
      if (btn.classList.contains(btnClass)) {
        this.openPopup(this.classListMap[btnClass]);
        return;
      }
    }
  }

  // ===== submit handlers =====
  async onSubmit(e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    const popup = form.closest(".tf-mini-cart-tool-openable");
    if (!popup) return;

    if (!popup.closest("cart-drawer")) return;

    if (form.dataset.submitting === "1") {
      e.preventDefault();
      return;
    }

    e.preventDefault();

    if (popup.classList.contains("add-gift")) {
      const giftVariantId = this.getGiftVariantIdFromPopup(popup);
      if (!giftVariantId) return;

      try {
        form.dataset.submitting = "1";

        const giftItem = await this.getGiftItem(giftVariantId);

        if (giftItem) {
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
            body: JSON.stringify({ id: giftItem.key, quantity: 0 }),
          });
        } else {
          await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
            body: JSON.stringify({ id: giftVariantId, quantity: 1 }),
          });
        }

        await this.refreshDrawer();

        const cart = await fetch("/cart.js", { credentials: "same-origin" }).then((r) => r.json());
        window.dispatchEvent(new CustomEvent("tf:cart:updated", { detail: { cart } }));

        const newPopup = document.querySelector(".tf-mini-cart-tool-openable.add-gift");
        if (newPopup) await this.updateGiftButton(newPopup);

        this.closeAllPopups();
      } catch (err) {
        console.error(err);
      } finally {
        delete form.dataset.submitting;
      }
      return;
    }

    if (popup.classList.contains("add-note")) {
      const noteEl = form.querySelector("#Cart-note") || form.querySelector("textarea[name='note']");
      const note = noteEl?.value || "";

      const statusEl = form.querySelector("[data-note-status]");
      const setStatus = (msg, ok = true) => {
        if (!statusEl) return;
        statusEl.textContent = msg;
        statusEl.style.color = ok ? "green" : "red";
      };

      try {
        setStatus("");

        const res = await fetch("/cart/update.js", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ note }),
        });

        if (!res.ok) throw new Error("Cart note update failed");

        setStatus("Note saved successfully !", true);

        const cart = await fetch("/cart.js", { credentials: "same-origin" }).then((r) => r.json());
        window.dispatchEvent(new CustomEvent("tf:cart:updated", { detail: { cart } }));
      } catch (err) {
        console.error(err);
        setStatus("There was an error saving your note.", false);
      }
      return;
    }

    // shipping submit giữ nguyên của bạn
  }

  escapeHTML(str) {
    return String(str ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }
}

customElements.define("cart-drawer-tools", CartDrawerTools);
